<script lang="ts">
  // List-Unsubscribe bar (spec §10): idle/busy → button; one-click and mailto results → done
  // message; manual → message + external link; failures → message + retry button. The mailto
  // path (no one-click) asks for confirmation first because it sends mail on the user's behalf.
  import { api, ApiError } from '$lib/api/client';
  import type { MessageDetail } from '$lib/api/types';
  import { t } from '$lib/i18n';
  import { confirm } from '$lib/store';

  type Unsub = NonNullable<MessageDetail['unsubscribe']>;

  let { folder, uid, unsub }: { folder: string; uid: number; unsub: Unsub } = $props();

  let phase = $state<'idle' | 'busy' | 'done' | 'manual' | 'failed'>('idle');
  let msg = $state('');
  let url = $state('');

  async function run(): Promise<void> {
    if (phase === 'busy') return;
    if (!unsub.oneClick && unsub.mailto) {
      const ok = await confirm({ title: t('unsubscribe'), body: t('unsubConfirm'), ok: t('send') });
      if (!ok) return;
    }
    phase = 'busy';
    msg = '';
    try {
      const r = await api.unsubscribe(folder, uid);
      if (r.ok && r.method === 'one-click') {
        phase = 'done';
        msg = t('unsubDone');
      } else if (r.ok && r.method === 'mailto') {
        phase = 'done';
        msg = t('unsubMailed', { to: r.to ?? '' });
      } else if (r.method === 'manual' && r.url) {
        phase = 'manual';
        url = r.url;
        msg = t('unsubManual');
      } else {
        phase = 'failed';
        msg = t('unsubFailed');
      }
    } catch (err) {
      phase = 'failed';
      msg = t(err instanceof ApiError && err.code === 'insecure_url' ? 'unsubInsecure' : 'unsubFailed');
    }
  }
</script>

<div class="unsub" data-state={phase} data-oneclick={unsub.oneClick ? '1' : '0'}>
  {#if phase === 'done'}
    <span class="unsub-msg">{msg}</span>
  {:else if phase === 'manual'}
    <span class="unsub-msg">{msg}</span>
    <a class="unsub-btn" target="_blank" rel="noopener noreferrer nofollow" href={url}>{t('unsubOpen')}</a>
  {:else}
    {#if msg}<span class="unsub-msg">{msg}</span>{/if}
    <button type="button" class="unsub-btn" disabled={phase === 'busy'} onclick={run}>{t(phase === 'busy' ? 'unsubBusy' : 'unsubscribe')}</button>
  {/if}
</div>

<style>
  /* svelte-rwh28b (Reader) — unsubscribe bar */
  .unsub {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-3);
    margin-top: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-subtle);
    font-size: var(--text-sm)
  }

  .unsub-msg {
    color: var(--text-muted)
  }

  .unsub-btn {
    margin-inline-start: auto;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-body);
    padding: var(--space-1) var(--space-4);
    min-block-size: var(--control-md);
    font: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    text-decoration: none
  }

  .unsub-btn:disabled {
    opacity: .6;
    cursor: default
  }

  .unsub[data-state=done] {
    border-color: var(--border-accent)
  }
</style>
